import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { UtilsService } from 'app/shared/utils.service';
import { RootEntityContextService } from './root-entity-context.service';
import {
  BNB_SCORE_BASE,
  DEFAULT_SCORE_BASE,
  SCORE_BASES,
  DEFAULT_MATURITY_RANGES,
  FIVE_SCALE_MATURITY_RANGES,
} from './constant/scoreBase.constant';

export interface ScoreBase {
  [key: string]: number;
}

@Injectable({
  providedIn: 'root',
})
export class ScoreService {
  private static entityBaseCache: ScoreBase = {}; //* Cache for entity score bases
  //* Backing value for activeScoreBase - static (not per-instance) so the static
  private static currentScoreBase: number = DEFAULT_SCORE_BASE;

  private readonly BNB_SCORE_BASE: number = BNB_SCORE_BASE; //* Fixed base for BNB instance
  private readonly DEFAULT_SCORE_BASE: number = DEFAULT_SCORE_BASE; //* Default base if none found

  constructor(
    private router: Router,
    private rootEntityContextService: RootEntityContextService
  ) {}

  public initScoreService(): void {
    this.listenToRouteParams();
  }

  //* Listen to route parameter changes to update score base accordingly
  private listenToRouteParams(): any {
    this.router.events
      .pipe(
        // 1. Only run this when navigation finishes
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        // 2. Traverse the route tree to find the deepest child
        let currentRoute = this.router.routerState.root;
        let params: any = {};
        while (currentRoute) {
          params = { ...params, ...currentRoute.snapshot.params };
          currentRoute = currentRoute.firstChild;
        }

        //* Initialize score base for the new entity ID
        if (params.entityId) {
          this.initializeEntityScoreBase(params.entityId);
        }
      });
  }

  //* Fetch root entity from database
  private async getRootEntityFromDb(entityId: string): Promise<any> {
    const entity = await this.rootEntityContextService.getRootEntityById(entityId);
    return entity || null;
  }

  //* Initialize score base for a given entity ID with caching
  public async initializeEntityScoreBase(entityId: string): Promise<void> {
    //* Check cache first
    if (ScoreService.entityBaseCache[entityId]) {
      this.updateScoreBase(ScoreService.entityBaseCache[entityId]);
      return;
    }
    //* Fetch from database if not in cache
    const entity = await this.getRootEntityFromDb(entityId);
    const scoreBase = entity?.scoringBase ?? this.DEFAULT_SCORE_BASE;
    this.updateScoreBase(scoreBase);
    ScoreService.entityBaseCache[entityId] = scoreBase; //* Cache the fetched base
  }

  public updateEntityScoreBaseInCache(entityId: string, newBase: number, initialize: boolean = false): void {
    ScoreService.entityBaseCache[entityId] = newBase;
    if (initialize) {
      this.updateScoreBase(newBase);
    }
  }

  public getEntityScoreBaseFromCache(entityId: string): number | null {
    return ScoreService.entityBaseCache[entityId] || DEFAULT_SCORE_BASE;
  }

  //* Instance accessor delegates to the static getter
  public get activeScoreBase(): number {
    return ScoreService.activeScoreBase;
  }

  //* Static accessor - lets the static UtilsService.calculateMaturityScore resolve the
  public static get activeScoreBase(): number {
    if (UtilsService.isBnB) {
      return BNB_SCORE_BASE;
    }
    return ScoreService.currentScoreBase || DEFAULT_SCORE_BASE;
  }

  //* Update current score base
  private updateScoreBase(newBase: number): void {
    ScoreService.currentScoreBase = newBase;
  }

  //* Pure, DI-free conversion of a raw score (on sourceBase, defaults to the 0-10 internal
  //* representation) down to any target base. Single source of truth for score scaling so
  //* getScaledScore and calculateMaturity don't duplicate this math.
  public static scaleScore(score: number, targetBase: number, sourceBase: number = DEFAULT_SCORE_BASE): number {
    if (!UtilsService.isDefined(score) || !UtilsService.isDefined(targetBase) || !sourceBase) {
      return 0;
    }
    return (score / sourceBase) * targetBase;
  }

  /**
   * Single source of truth for computing an entity's maturity level.
   * @param total is the raw score, always represented internally on a 0-10 scale.
   * Resolves the active scoring base itself via ScoreService.activeScoreBase - callers
   * (e.g. UtilsService.calculateMaturityScore) don't need to pass it in. For the default
   * 0-10 base and BnB/Mid-market/Beecher, this uses the CMMI-style bands
   * (DEFAULT_MATURITY_RANGES). When the active base is 0-5, the raw score is scaled
   * down to that base and matched against the equal-width bands
   * (FIVE_SCALE_MATURITY_RANGES) instead, so a score displayed as e.g. "1.4/5"
   */
  public static calculateMaturity(total: number): number {
    if (!UtilsService.isDefined(total)) {
      return 0;
    }

    total = +total.toFixed(1);

    const useBnbModel = UtilsService.isBnB || UtilsService.isMidMarket || UtilsService.isBnBCyberSite;
    const scoreBase = ScoreService.activeScoreBase;
    const isFiveScoreBase = !useBnbModel && scoreBase === SCORE_BASES.FIVE;

    const scaledTotal = isFiveScoreBase ? +ScoreService.scaleScore(total, scoreBase).toFixed(1) : total;

    const bands = isFiveScoreBase ? FIVE_SCALE_MATURITY_RANGES : DEFAULT_MATURITY_RANGES;
    const maturity = bands.find(item => scaledTotal <= item.range);

    if (maturity) {
      return maturity.value;
    }

    // Out-of-range / NA-sentinel totals (e.g. 998, 999) always map to "no maturity" (0),
    return 0;
  }

  //* return grid lines based on active score base
  public getGridLines(): number[] {
    const base = this.activeScoreBase;
    const gridLines: number[] = [];
    for (let i = base; i >= 0; i--) {
      gridLines.push(i);
    }
    return gridLines;
  }

  public getScaledScore(
    score: any,
    isThirdParty: boolean = false,
    customBase: number | null = null,
    isTarget: boolean = false
  ): string {
    const fallbackBase = isThirdParty ? this.DEFAULT_SCORE_BASE : (this.activeScoreBase ?? this.DEFAULT_SCORE_BASE);
    const currentBase = customBase ?? fallbackBase;

    if (!UtilsService.isDefined(score)) {
      return (0.0).toFixed(1);
    }

    const numb = parseFloat(score);
    const isString = typeof score === 'string';

    const convertedScore = ScoreService.scaleScore(numb, currentBase);

    // Use truncation to 1 decimal (avoid rounding up 4.95 -> 5.0).
    const truncatedTo1Decimal = Math.floor(convertedScore * 10) / 10;

    // When score equals the base (max score), show as integer without decimals
    if (truncatedTo1Decimal === currentBase) {
      return currentBase.toString();
    }

    // Format result based on input type and value
    if (isString) {
      return truncatedTo1Decimal.toFixed(1);
    }
    //* For target scores, we want to show as integer if it's a whole number (e.g. 5 instead of 5.0)
    if (isTarget) {
      return Math.floor(truncatedTo1Decimal).toString();
    }

    if (truncatedTo1Decimal === 0) {
      return (0.0).toFixed(1);
    }

    return truncatedTo1Decimal.toFixed(1);
  }
}
